import { useEffect, useRef, useState } from "react";
import { Dialog, DialogClose, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";

interface LightboxImage {
  src: string;
  width?: number;
  height?: number;
  alt: string;
  caption?: string;
}

interface ImageLightboxProps {
  /** Full-size image URL shown in the enlarged overlay. */
  fullSrc: string;
  fullWidth?: number;
  fullHeight?: number;
  alt: string;
  caption?: string;
  enlargeLabel?: string;
  closeLabel?: string;
  lang?: "en" | "es";
  images?: LightboxImage[];
  children: React.ReactNode;
}

/**
 * Click-to-enlarge wrapper: renders its children inside a button that opens a
 * modal with the full-size image. Close via the X, the backdrop, or Escape.
 */
export function ImageLightbox({
  fullSrc,
  fullWidth,
  fullHeight,
  alt,
  caption,
  enlargeLabel = `Enlarge image: ${alt}`,
  closeLabel = "Close enlarged image",
  lang = "en",
  images,
  children,
}: ImageLightboxProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const imageReady = useRef<Promise<void> | null>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const gallery = images?.length ? images : [{ src: fullSrc, width: fullWidth, height: fullHeight, alt, caption }];
  const activeImage = gallery[activeIndex] ?? gallery[0];
  const hasMultipleImages = gallery.length > 1;

  useEffect(() => {
    if (!open || !hasMultipleImages) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        setActiveIndex((current) => (current - 1 + gallery.length) % gallery.length);
      } else if (event.key === "ArrowRight") {
        setActiveIndex((current) => (current + 1) % gallery.length);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [gallery.length, hasMultipleImages, open]);

  const preloadImage = () => {
    if (!imageReady.current) {
      imageReady.current = new Promise<void>((resolve) => {
        const image = new Image();
        image.src = fullSrc;
        if (image.complete) {
          resolve();
          return;
        }
        image.onload = () => resolve();
        image.onerror = () => resolve();
      });
    }
    return imageReady.current;
  };

  const openLightbox = async () => {
    if (hasMultipleImages) {
      setActiveIndex(0);
      setOpen(true);
      return;
    }
    await preloadImage();
    setActiveIndex(0);
    setOpen(true);
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        onClick={() => void openLightbox()}
        onPointerEnter={preloadImage}
        onFocus={preloadImage}
        aria-label={enlargeLabel}
        className="group relative block h-auto min-h-0 w-full cursor-zoom-in whitespace-normal rounded-lg p-0 focus-visible:ring-2 focus-visible:ring-ring"
      >
        {children}
        <span
          aria-hidden="true"
          className="absolute bottom-1.5 right-1.5 rounded-md bg-background/80 p-1 text-foreground opacity-100 [@media(hover:hover)]:bottom-2 [@media(hover:hover)]:right-2 [@media(hover:hover)]:p-1.5 [@media(hover:hover)]:transition-opacity [@media(hover:hover)]:group-hover:opacity-100 group-focus-visible:opacity-100 lg:[@media(hover:hover)]:opacity-0"
        >
          <ZoomIn className="h-3.5 w-3.5 [@media(hover:hover)]:h-4 [@media(hover:hover)]:w-4" />
        </span>
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          showCloseButton={false}
          className="flex h-[96dvh] max-h-[96dvh] w-[96vw] max-w-6xl flex-col gap-2 overflow-hidden p-2 data-[state=open]:animate-none sm:p-4"
          onTouchStart={(event) => {
            if (!hasMultipleImages) return;
            const touch = event.touches[0];
            if (touch) touchStart.current = { x: touch.clientX, y: touch.clientY };
          }}
          onTouchEnd={(event) => {
            if (!hasMultipleImages || !touchStart.current) return;
            const touch = event.changedTouches[0];
            if (!touch) return;
            const dx = touch.clientX - touchStart.current.x;
            const dy = touch.clientY - touchStart.current.y;
            touchStart.current = null;
            if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy)) return;
            setActiveIndex((current) => (current + (dx < 0 ? 1 : gallery.length - 1)) % gallery.length);
          }}
        >
          <DialogTitle className="sr-only">{activeImage?.alt ?? alt}</DialogTitle>
          <DialogClose asChild>
            <Button
              type="button"
              variant="secondary"
              size="icon"
              aria-label={closeLabel}
              className="absolute right-3 top-3 z-10 min-h-11 w-11 shadow"
            >
              <X aria-hidden="true" />
            </Button>
          </DialogClose>
          {activeImage ? (
            <div className="flex min-h-0 flex-1 items-center justify-center">
              <img
                src={activeImage.src}
                alt={activeImage.alt}
                width={activeImage.width}
                height={activeImage.height}
                className="max-h-full w-auto max-w-full rounded-md object-contain"
              />
            </div>
          ) : null}
          {hasMultipleImages ? (
            <nav className="mx-auto flex w-full max-w-4xl items-center justify-between gap-3" aria-label={lang === "es" ? "Imágenes del paciente" : "Patient story images"}>
              <Button
                type="button"
                variant="outline"
                size="icon"
                 aria-label={lang === "es" ? "Imagen anterior" : "Previous image"}
                onClick={() => setActiveIndex((current) => (current - 1 + gallery.length) % gallery.length)}
              >
                <ChevronLeft aria-hidden="true" />
              </Button>
               <span className="text-sm text-muted-foreground">{activeIndex + 1} {lang === "es" ? "de" : "of"} {gallery.length}</span>
              <Button
                type="button"
                variant="outline"
                size="icon"
                 aria-label={lang === "es" ? "Imagen siguiente" : "Next image"}
                onClick={() => setActiveIndex((current) => (current + 1) % gallery.length)}
              >
                <ChevronRight aria-hidden="true" />
              </Button>
            </nav>
          ) : null}
          {activeImage?.caption ? (
            <p className="mx-auto max-w-4xl text-center text-xs text-muted-foreground">{activeImage.caption}</p>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
